import React, { type ReactNode, useCallback, useState } from 'react';
import { AppHeader, AppShell, Button, ShellPanel, SideNav, SideNavGroup, SideNavItem } from '@origo/origo-ui';
import {
    LinkIcon,
    DatabaseIcon,
    PulseIcon,
    GearSixIcon,
} from '@phosphor-icons/react';
import { VersionTrigger, ChangelogDialog, NewVersionNotice, useNewVersionNotice } from '@origo/origo-components';
import { useAppSelector, selectUser } from '../store/storeHooks';
import { BindingsPanel } from '../features/bindings/BindingsPanel';
import { DatasetsPanel } from '../features/datasets/DatasetsPanel';
import { StatusPanel } from '../features/status/StatusPanel';
import { SettingsPanel } from '../features/settings/SettingsPanel';
import geodataLogo from '../assets/geodata-logo.svg';
import changelogMarkdown from '../../CHANGELOG.md?raw';
import styles from './AppLayout.module.css';

export interface AppLayoutProps {
    mapContent: ReactNode;
    onSignOut?: () => void;
    defaultSystem: string;
}

type ViewId = 'bindings' | 'datasets' | 'status' | 'settings';

const ICON_SIZE = 20;

function getInitials(name: string): string {
    return name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();
}

const AppLayout: React.FC<AppLayoutProps> = ({ mapContent, onSignOut, defaultSystem }) => {
    const user = useAppSelector(selectUser);
    const [view, setView] = useState<ViewId>('bindings');
    const [panelWidth, setPanelWidth] = useState(() => Math.floor(window.innerWidth * 0.34));
    const [changelogOpen, setChangelogOpen] = useState(false);
    const { hasNewVersion, dismiss } = useNewVersionNotice('synkadmin.lastSeenVersion');

    const handlePanelResize = useCallback((width: number) => setPanelWidth(width), []);

    const openChangelog = () => {
        setChangelogOpen(true);
        dismiss();
    };

    return (
        <AppShell
            className={styles.adminShell}
            header={
                <AppHeader
                    logo={<img src={geodataLogo} alt="Geodata" width={34} height={32} />}
                    title="Synkadmin"
                    subtitle="Synkronisering AGOL ↔ forvaltning"
                    user={
                        user?.fullName
                            ? {
                                  name: user.fullName,
                                  initials: getInitials(user.fullName),
                                  avatarUrl: user.thumbnail,
                              }
                            : undefined
                    }
                    actions={
                        <>
                            <VersionTrigger placement="header" onClick={openChangelog} />
                            {onSignOut && (
                                <Button variant="ghost" size="sm" onClick={onSignOut}>
                                    Logg ut
                                </Button>
                            )}
                        </>
                    }
                />
            }
        >
            {hasNewVersion && (
                <NewVersionNotice onOpenChangelog={openChangelog} onDismiss={dismiss} />
            )}
            <div className={styles.body}>
                <ShellPanel
                    actionBar={
                        <SideNav
                            activeId={view}
                            onSelect={(id) => setView(id as ViewId)}
                            collapsed
                        >
                            <SideNavGroup>
                                <SideNavItem
                                    id="bindings"
                                    icon={<LinkIcon size={ICON_SIZE} weight="duotone" />}
                                    description="Koblinger mellom AGOL-tjenester og forvaltningssystemet."
                                >
                                    Koblinger
                                </SideNavItem>
                                <SideNavItem
                                    id="datasets"
                                    icon={<DatabaseIcon size={ICON_SIZE} weight="duotone" />}
                                    description="Søk opp datasett i et forvaltningssystem."
                                >
                                    Datasett
                                </SideNavItem>
                                <SideNavItem
                                    id="status"
                                    icon={<PulseIcon size={ICON_SIZE} weight="duotone" />}
                                    description="Helse, synkkø og tidsstyrt synk."
                                >
                                    Status
                                </SideNavItem>
                            </SideNavGroup>
                            <SideNavGroup label="System">
                                <SideNavItem
                                    id="settings"
                                    icon={<GearSixIcon size={ICON_SIZE} weight="duotone" />}
                                    description="API-tilkobling og token."
                                >
                                    Innstillinger
                                </SideNavItem>
                            </SideNavGroup>
                        </SideNav>
                    }
                    resizable
                    defaultWidth={panelWidth}
                    minWidthVw={28}
                    maxWidthVw={60}
                    onResize={handlePanelResize}
                >
                    <div className={styles.panelHost}>
                        {view === 'bindings' && <BindingsPanel defaultSystem={defaultSystem} />}
                        {view === 'datasets' && <DatasetsPanel defaultSystem={defaultSystem} />}
                        {view === 'status' && <StatusPanel />}
                        {view === 'settings' && <SettingsPanel />}
                    </div>
                </ShellPanel>

                <div className={styles.mapArea}>
                    <div className={styles.mapContainer}>{mapContent}</div>
                </div>
            </div>

            <ChangelogDialog
                open={changelogOpen}
                onClose={() => setChangelogOpen(false)}
                appName="Synkadmin"
                changelogMarkdown={changelogMarkdown}
            />
        </AppShell>
    );
};

export default AppLayout;
